import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import { getPlayerById, getTeamById } from "@/utils/nba";
import TeamBadge from "@/components/base/TeamBadge";

export default function Player() {
  const { playerId } = useParams();
  const player = getPlayerById(playerId);
  const team = getTeamById(player?.teamId);

  useSeo({
    title: player ? `${player.name}球员资料与数据统计 - NBA直播吧` : "球员资料 - NBA直播吧",
    description: player
      ? `${player.name}（${player.position}，身着 ${player.number} 号）球员资料页，包含身高体重、国籍等基本信息，以及本赛季场均 ${player.seasonStats.ppg} 分 ${player.seasonStats.rpg} 篮板 ${player.seasonStats.apg} 助攻的数据统计。`
      : "NBA 球员资料页。",
    keywords: player ? `${player.name},NBA球员数据,NBA球员资料,${player.position},NBA直播吧` : "NBA球员资料",
  });

  if (!player) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-user-line text-2xl" />
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该球员</h1>
        <Link to="/teams" className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer">
          返回球队与球员
        </Link>
      </div>
    );
  }

  const age = 2026 - player.birthYear;

  const seasonRows = [
    { label: "出场", value: player.seasonStats.games },
    { label: "场均得分", value: player.seasonStats.ppg },
    { label: "场均篮板", value: player.seasonStats.rpg },
    { label: "场均助攻", value: player.seasonStats.apg },
    { label: "场均抢断", value: player.seasonStats.spg },
    { label: "场均盖帽", value: player.seasonStats.bpg },
    { label: "投篮命中率", value: `${player.seasonStats.fg}%` },
  ];

  const careerRows = [
    { label: "生涯赛季", value: player.careerStats.seasons },
    { label: "生涯场均得分", value: player.careerStats.ppg },
    { label: "生涯场均篮板", value: player.careerStats.rpg },
    { label: "生涯场均助攻", value: player.careerStats.apg },
    { label: "入选全明星", value: `${player.careerStats.allStar} 次` },
  ];

  const infoRows = [
    { label: "身高", value: player.height },
    { label: "体重", value: player.weight },
    { label: "国籍", value: player.nationality },
    { label: "年龄", value: `${age} 岁` },
    { label: "球龄", value: `${player.experience} 年` },
    { label: "位置", value: player.position },
  ];

  return (
    <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/teams" className="hover:text-primary-700 cursor-pointer">球队与球员</Link>
        {team && (
          <>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/team/${team.id}`} className="hover:text-primary-700 cursor-pointer">{team.fullName}</Link>
          </>
        )}
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900 font-medium">{player.name}</span>
      </nav>

      <div className="mb-8 flex flex-col gap-6 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:p-6">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <span className="font-heading text-3xl font-bold">{player.number}</span>
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{player.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">{player.position}</span>
            {team && (
              <Link to={`/team/${team.id}`} className="flex items-center gap-2 rounded-md bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 hover:text-primary-700 cursor-pointer">
                <TeamBadge team={team} size="sm" />
                {team.fullName}
              </Link>
            )}
            <span className="text-xs text-foreground-500">场均 {player.seasonStats.ppg} 分 · {player.seasonStats.rpg} 篮板 · {player.seasonStats.apg} 助攻</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">本赛季数据统计</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {seasonRows.map((row) => (
              <div key={row.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <p className="font-heading text-xl font-bold text-primary-700">{row.value}</p>
                <p className="mt-1 text-xs text-foreground-500">{row.label}</p>
              </div>
            ))}
          </div>

          <h2 className="mb-4 mt-8 font-heading text-xl font-bold text-foreground-950">职业生涯统计</h2>
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {careerRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between border-b border-background-200 px-4 py-3 text-sm last:border-b-0">
                <span className="text-foreground-600">{row.label}</span>
                <span className="font-heading font-semibold text-foreground-950">{row.value}</span>
              </div>
            ))}
          </div>

          <h2 className="mb-4 mt-8 font-heading text-xl font-bold text-foreground-950">相关资讯</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {topics.slice(0, 4).map((topic) => (
              <Link
                key={topic.id}
                to={topic.link}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:border-primary-300 cursor-pointer"
              >
                <div className="h-32 w-full overflow-hidden">
                  <img src={topic.image} alt={topic.title} title={`${topic.title} - NBA直播吧`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-950 group-hover:text-primary-700 line-clamp-2">{topic.title}</p>
                  <p className="mt-2 text-xs text-foreground-400">{topic.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">基本资料</h2>
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {infoRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between border-b border-background-200 px-4 py-3 text-sm last:border-b-0">
                <span className="text-foreground-600">{row.label}</span>
                <span className="font-medium text-foreground-950">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}